import React, { useContext } from 'react';
import type { FC } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import { N300A, N400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { SPAViewContext } from '@confluence/spa-view-context';
import { PremiumFeatureHighlightToggle } from '@confluence/premium-onboarding/entry-points/PremiumFeatureHighlightToggle';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const MenuItemDivider = styled.hr({
	width: '100%',
	background: token('color.border', N300A),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PremiumFeatureHighlightToggleTitle = styled.div({
	fontSize: 12,
	fontStyle: 'normal',
	fontWeight: 600,
	lineHeight: '16px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PremiumFeatureHighlightToggleBody = styled.div({
	fontSize: 11,
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '14px',
	color: token('color.text.subtlest', N400),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PremiumFeatureHighlightToggleContent = styled.label({
	width: '220px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PremiumFeatureHighlightToggleContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: `${token('space.100', '8px')} ${token('space.250', '20px')}`,
});

import { getPillType } from './getPillType';

interface PremiumMenuHighlightProps {
	hasBillingInformation: boolean;
	isPTORTEnabled?: boolean;
}

const i18n = defineMessages({
	featureHighlightTitle: {
		id: 'trial-components.premium.feature-highlight-title.toggle',
		defaultMessage: 'Highlight Premium features',
		description: 'Premium feature highlight toggle title',
	},
	featureHighlightDescription: {
		id: 'trial-components.premium.feature-highlight-description.toggle',
		defaultMessage: 'We’ll help you identify Premium features with a label.',
		description: 'Premium feature highlight toggle description',
	},
});

export const PremiumMenuHighlight: FC<PremiumMenuHighlightProps> = ({
	hasBillingInformation,
	isPTORTEnabled = false,
}) => {
	const { isSiteAdmin } = useContext(SPAViewContext);
	const pillType = getPillType({ isSiteAdmin, hasBillingInformation });

	return (
		<>
			<MenuItemDivider />
			<PremiumFeatureHighlightToggleContainer>
				<PremiumFeatureHighlightToggleContent htmlFor="prem-feature-highlight-toggle-pill">
					<PremiumFeatureHighlightToggleTitle>
						<FormattedMessage {...i18n.featureHighlightTitle} />
					</PremiumFeatureHighlightToggleTitle>
					<PremiumFeatureHighlightToggleBody>
						<FormattedMessage {...i18n.featureHighlightDescription} />
					</PremiumFeatureHighlightToggleBody>
				</PremiumFeatureHighlightToggleContent>
				<PremiumFeatureHighlightToggle
					id="prem-feature-highlight-toggle-pill"
					source="premTrialActionComponent"
					pillType={pillType}
					isPTORTEnabled={isPTORTEnabled}
				/>
			</PremiumFeatureHighlightToggleContainer>
			{isSiteAdmin && <MenuItemDivider />}
		</>
	);
};
